import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Users, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Teams({ user, logout }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);

  useEffect(() => {
    fetchTeams();
    fetchMyRequests();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await axios.get(`${API}/my-join-requests`, {
        withCredentials: true
      });
      setMyRequests(response.data);
    } catch (error) {
      console.error("Error fetching my requests:", error);
    }
  };

  const joinTeam = async (teamId) => {
    try {
      const response = await axios.post(`${API}/teams/${teamId}/join`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      await Promise.all([fetchTeams(), fetchMyRequests()]);
    } catch (error) {
      console.error("Error joining team:", error);
      toast.error(error.response?.data?.detail || "Failed to join team");
    }
  };

  const getRequestStatus = (teamId) => {
    const request = myRequests.find(r => r.team_id === teamId && r.status === "pending");
    return request ? "pending" : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5 px-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-wide font-display">
              SCC Football
            </h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-display" data-testid="page-title">
          Teams
        </h2>

        {teams.length === 0 ? (
          <Card className="p-8 text-center bg-slate-900/50 border border-white/5" data-testid="no-teams-message">
            <p className="text-slate-400">No teams available yet. Check back soon!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => {
              const isMember = team.members?.includes(user.id);
              const isPending = getRequestStatus(team.id) === "pending";
              
              return (
                <Card
                  key={team.id}
                  className="group relative overflow-hidden bg-slate-900/50 border border-white/5 rounded-xl p-6 hover:border-yellow-500/50 transition-all duration-300 cursor-pointer"
                  data-testid={`team-card-${team.id}`}
                  onClick={() => navigate(`/teams/${team.id}`)}
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-slate-900 to-amber-600 rounded-xl mb-4">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-yellow-400 transition-colors" data-testid={`team-name-${team.id}`}>
                    {team.name}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    {team.member_count ?? team.members?.length ?? 0} members
                  </p>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      joinTeam(team.id);
                    }}
                    className={`w-full font-semibold transition-all ${
                      isMember 
                        ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/100/30 cursor-default" 
                        : isPending
                        ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/100/30 cursor-default"
                        : "bg-yellow-500/100 text-black hover:bg-yellow-400"
                    }`}
                    data-testid={`join-team-button-${team.id}`}
                    disabled={isMember || isPending}
                  >
                    {isMember ? "✓ Joined" : isPending ? "Request Pending" : "Request to Join"}
                  </Button>
                  <ChevronRight className="absolute top-6 right-6 w-5 h-5 text-slate-600 group-hover:text-yellow-500 transition-colors" />
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
